import React, { useEffect, useState } from "react";
import LoginIntro from "./LoginIntro";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import {
  getStaffByEmpCode,
  passwordLogin,
  staffPasswordLogin,
} from "../../features/user/UserSlice";
import { toast } from "sonner";
import Tabs from "../../components/Tabs";
import { Link } from "react-router-dom";
import { popupNavigation } from "../../util/functions/UniversalFunctions";
import ForgottenPassword from "../../components/modal/ForgottenPassword";

export default function Login() {
  const [authMode, setAuthMode] = useState<"STAFF" | "ADMIN">("STAFF");
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [password, setPassword] = useState("");
  const [staffId, setStaffId] = useState<number | null>(null);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [loginIdentifier, setLoginIdentifier] = useState(""); // email or branchCode
  const [loginType, setLoginType] = useState<"EMAIL" | "BRANCH">("EMAIL");

  const [employeeCode, setEmployeeCode] = useState("");
  const [step, setStep] = useState<"EMP_CODE" | "LOGIN">("EMP_CODE");

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { user, isLoadingUser } = useSelector((state: RootState) => state.user);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    //  ADMIN LOGIN
    if (authMode === "ADMIN") {
      if (!emailOrMobile || !password) return;

      await dispatch(
        passwordLogin({
          user: { emailOrMobile, password },
        })
      );

      return;
    }

    // STAFF LOGIN
    if (step === "EMP_CODE") {
      if (!employeeCode) return;

      const result = await dispatch(
        getStaffByEmpCode({ empCode: employeeCode })
      );

      if (getStaffByEmpCode.fulfilled.match(result)) {
        const staffList = result.payload;

        if (!staffList || staffList.length === 0) {
          toast.error("Invalid employee code");
          return;
        }

        const staffListFromApi = result.payload;

        if (!staffListFromApi || staffListFromApi.length === 0) {
          toast.error("Invalid employee code");
          return;
        }

        // filter active staff only
        const activeStaff = staffListFromApi.filter((s: any) => !s.isDeleted);

        if (activeStaff.length === 0) {
          toast.error("Employee is inactive");
          return;
        }

        setStaffList(activeStaff);

        // if only one staff → auto select
        if (activeStaff.length === 1) {
          const staff = activeStaff[0];
          setEmailOrMobile(staff.email || "");
          setStaffId(staff.staffId);
        } else {
          // multiple staff → user must select
          setEmailOrMobile("");
          setStaffId(null);
        }

        setStep("LOGIN");
        return;
      }

      toast.error("Failed to verify employee code");
      return;
    }

    if (!password) {
      toast.error("Please enter password");
      return;
    }

    if (!staffId) {
      toast.error(
        loginType === "EMAIL"
          ? "Invalid email selected"
          : "Invalid branch code selected"
      );
      return;
    }

    await dispatch(staffPasswordLogin({ staffId, password }));
  };

  useEffect(() => {
    if (user?.accessToken) {
      navigate("/");
    }
  }, [user, navigate]);

  const isDisabled =
    authMode === "ADMIN"
      ? !emailOrMobile || !password || isLoadingUser
      : step === "EMP_CODE"
      ? !employeeCode || isLoadingUser
      : !password || isLoadingUser;

  return (
    <div className="w-full md:min-h-screen gap-14 md:px-10 xl:px-0 max-w-6xl mx-auto grid lg:grid-cols-2 lg:justify-center pb-14 md:pb-10 md:py-10 items-center ">
      <div>
        <LoginIntro />
      </div>
      <div className="gap-9 text-center lg:py-12 px-6 md:px-14 flex flex-col justify-center items-center">
        {/* <h4 className="uppercase text-2xl md:text-3xl font-semibold tracking-[0.6px] text-primary-500">
          SCHOOL LOGO
        </h4> */}
        <div className="flex flex-col gap-3">
          <p className="text-3xl md:text-4xl xl:text-5xl font-semibold text-neutral-400">
            Welcome Back!
          </p>
          <p className="text-sm font-medium text-neutral-300">
            Great to see you again! Sign in to keep your school operations
            running smoothly.
          </p>
        </div>

        {/* swtich between staff and admin */}
        <Tabs
          containerClass="flex flex-row text-nowrap gap-7 py-2 overflow-x-auto rounded-lg px-3  w-full sm:w-fit custom-scrollbar text-base font-semibold text-sm text-primary-500 bg-background-200"
          tabs={["Staff Login", "Admin Login"]}
          activeTab={authMode === "STAFF" ? "Staff Login" : "Admin Login"}
          onTabChange={(tab) => {
            if (tab === "Staff Login") {
              setAuthMode("STAFF");
              setStep("EMP_CODE");
              setPassword("");
              setEmailOrMobile("");
            } else {
              setAuthMode("ADMIN");
              setStep("LOGIN");
              setEmployeeCode("");
              setStaffList([]);
              setStaffId(null);
            }
          }}
        />

        <form className="w-full flex flex-col gap-14" onSubmit={handleLogin}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-6">
              {step === "EMP_CODE" && (
                <input
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                  placeholder="Employee Code"
                  className="bg-background-200 text-base placeholder:text-neutral-300 py-4 px-5 rounded-lg outline-none"
                />
              )}

              {step === "LOGIN" && staffList.length > 1 && (
                <>
                  <select
                    value={loginType}
                    onChange={(e) => {
                      setLoginType(e.target.value as "EMAIL" | "BRANCH");
                      setLoginIdentifier("");
                      setStaffId(null);
                    }}
                    className="bg-background-200 py-4 px-5 rounded-lg outline-none">
                    <option value="EMAIL">Login by Email</option>
                    <option value="BRANCH">Login by Branch Code</option>
                  </select>

                  <input
                    value={loginIdentifier}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLoginIdentifier(value);

                      const matchedStaff = staffList.find((s) =>
                        loginType === "EMAIL"
                          ? s.email === value
                          : s.branchCode === value
                      );

                      setStaffId(matchedStaff?.staffId || null);
                    }}
                    placeholder={
                      loginType === "EMAIL"
                        ? "Enter Email"
                        : "Enter Branch Code"
                    }
                    className="bg-background-200 text-base placeholder:text-neutral-300 py-4 px-5 rounded-lg outline-none"
                  />
                </>
              )}

              {step === "LOGIN" && (
                <>
                  {step === "LOGIN" && staffList.length <= 1 && (
                    <input
                      value={emailOrMobile}
                      onChange={(e) => setEmailOrMobile(e.target.value)}
                      placeholder="Email address"
                      readOnly={authMode === "STAFF"}
                      className="bg-background-200 text-base placeholder:text-neutral-300 py-4 px-5 rounded-lg outline-none"
                    />
                  )}

                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-background-200 text-base placeholder:text-neutral-300 py-4 px-5 rounded-lg outline-none"
                  />
                </>
              )}
            </div>

            {step === "LOGIN" && (
              <div className="flex justify-end items-end">
                <Link
                  to={popupNavigation("forgotenPassword")}
                  className="text-neutral-300 font-medium text-sm">
                  Forgot password?
                </Link>
              </div>
            )}
          </div>
          <button
            className={`py-4  ${
              isDisabled
                ? "bg-primary-100 cursor-not-allowed"
                : "bg-primary-600 hover:scale-105 cursor-pointer"
            } w-full rounded-lg text-base text-white px-10  `}
            type="submit">
            {authMode === "ADMIN"
              ? "Login as Admin"
              : step === "EMP_CODE"
              ? "Continue"
              : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}
